import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "@/components/ui/Text";

/** After this, the in-flight row says the turn is taking longer than usual. */
export const ANALYST_SLOW_AFTER_SECONDS = 45;

function useElapsedSeconds(active: boolean, startedAt: string | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !startedAt) {
      return;
    }
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [active, startedAt]);

  if (!active || !startedAt) {
    return 0;
  }
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) {
    return 0;
  }
  return Math.max(0, Math.floor((now - started) / 1000));
}

interface AnalystElapsedProps {
  active: boolean;
  startedAt: string | null;
}

export function AnalystElapsed(props: AnalystElapsedProps) {
  const { active, startedAt } = props;
  const { t } = useTranslation("app");
  const elapsedSeconds = useElapsedSeconds(active, startedAt);

  if (elapsedSeconds < 1) {
    return null;
  }

  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      data-testid="analyst-elapsed"
    >
      <Text as="span" size="sm" color="muted">
        {t("studyReport.analyst.elapsed", { count: elapsedSeconds })}
      </Text>
      {elapsedSeconds >= ANALYST_SLOW_AFTER_SECONDS ? (
        <Text as="span" size="sm" color="muted">
          {t("studyReport.analyst.thinkingSlow")}
        </Text>
      ) : null}
    </span>
  );
}
